/**
 * @fileoverview MonthNavigation component handles month selection with balance display
 * and navigation controls. Manages shared state for collapsible balance details on mobile.
 * Coordinates between BalanceDisplay, NavigationControls, and BankBalanceDisplay.
 */

import { useState } from 'react';
import BalanceDisplay from './BalanceDisplay';
import BankBalanceDisplay from './BankBalanceDisplay';
import NavigationControls from './NavigationControls';
import ActionButtons from './ActionButtons';
import styles from './MonthNavigation.module.css';

/**
 * MonthNavigation Component
 *
 * Handles month selection and navigation controls with balance displays.
 * Layout: Budget balance (left) | Navigation controls (center) | Bank balance (right)
 * On mobile, budget balance details can be collapsed/expanded.
 *
 * Props:
 * - selectedMonthDisplay (string): Display name for current month
 * - isCurrentMonth (boolean): Whether viewing the current month
 * - onPreviousMonth (function): Called when user clicks previous month
 * - onNextMonth (function): Called when user clicks next month
 * - onGoToCurrentMonth (function): Called when user clicks current month button
 * - isPrevDisabled (boolean): Whether previous button should be disabled
 * - isNextDisabled (boolean): Whether next button should be disabled
 * - isClosed (boolean): Whether the selected month is closed
 * - isClosing (boolean): Whether a close/open operation is in progress
 * - closeError (string): Error message if close/open failed
 * - onCloseMonth (function): Called when user clicks close month button
 * - onOpenMonth (function): Called when user clicks open month button
 * - endingBalance (number): Ending balance for the month
 * - startingBalance (number): Starting balance for the month
 * - currentBalance (number): Current balance (running total)
 * - balanceError (string): Error message if balance fetch failed
 * - bankBalance (number): Total bank account balance
 * - onUpdateBankBalance (function): Called when user clicks update bank balance button
 */
function MonthNavigation({
    selectedMonthDisplay,
    isCurrentMonth,
    onPreviousMonth,
    onNextMonth,
    onGoToCurrentMonth,
    isPrevDisabled,
    isNextDisabled,
    isClosed,
    isClosing,
    closeError,
    onCloseMonth,
    onOpenMonth,
    endingBalance,
    startingBalance,
    currentBalance,
    balanceError,
    bankBalance,
    onUpdateBankBalance
}) {
    // Shared state for collapsing balance details on mobile
    const [isExpanded, setIsExpanded] = useState(false);
    return (
        <div className={styles.container}>
            <div className={styles.layout}>
                {/* Left Side - Budget Balance Information */}
                <BalanceDisplay
                    endingBalance={endingBalance}
                    startingBalance={startingBalance}
                    currentBalance={currentBalance}
                    balanceError={balanceError}
                    isExpanded={isExpanded}
                    setIsExpanded={setIsExpanded}
                />

                {/* Center - Month Navigation Controls */}
                <NavigationControls
                    selectedMonthDisplay={selectedMonthDisplay}
                    isClosed={isClosed}
                    isPrevDisabled={isPrevDisabled}
                    isNextDisabled={isNextDisabled}
                    onPreviousMonth={onPreviousMonth}
                    onNextMonth={onNextMonth}
                />

                {/* Right Side - Bank Balance and Action Buttons */}
                <div className={styles.rightSection}>
                    <BankBalanceDisplay
                        bankBalance={bankBalance}
                        endingBalance={endingBalance}
                        onUpdateBankBalance={onUpdateBankBalance}
                        isExpanded={isExpanded}
                    />
                    <ActionButtons
                        isCurrentMonth={isCurrentMonth}
                        isClosed={isClosed}
                        isClosing={isClosing}
                        closeError={closeError}
                        onGoToCurrentMonth={onGoToCurrentMonth}
                        onCloseMonth={onCloseMonth}
                        onOpenMonth={onOpenMonth}
                    />
                </div>
            </div>
        </div>
    );
}

export default MonthNavigation;